import { db } from '../config/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import {
  MiniAppProductsConfig,
  MiniAppCategory,
  CukcukCategoryGroup,
  MiniAppForYouConfig,
  MiniAppSearchConfig,
  MiniAppCartConfig,
  MiniAppProfileConfig,
} from '../types/miniAppManagement';
import { DishService } from './dishService';
import { GroupService } from './groupService';
import { coffeeService } from './coffeeService';
import { bottledDrinkService } from './bottledDrinkService';
import { CoffeeEquipmentService } from './coffeeEquipmentService';
import { Dish } from '../types/dish';
import {
  CUKCUK_DISHES_COLLECTION,
  IPOS_DISHES_COLLECTION
} from '../constants/cukcuk';

const CONFIG_COLLECTION = 'miniapp_config';
const PRODUCTS_PAGE_DOC = 'products_page';
const FOR_YOU_PAGE_DOC = 'foryou_page';
const SEARCH_PAGE_DOC = 'search_page';
const CART_PAGE_DOC = 'cart_page';
const PROFILE_PAGE_DOC = 'profile_page';

// Predefined styling palettes for standard Cukcuk categories
const DEFAULT_CATEGORY_THEMES: Record<string, { color: string; gradient: string; subtitle: string; iconName: string; badge?: string }> = {
  'specialty': {
    color: '#8B4513',
    gradient: 'linear-gradient(135deg, #FFF8E7 0%, #FFE0B2 100%)',
    subtitle: 'Hạt cà phê đặc sản tuyển chọn',
    iconName: 'FireOutlined',
    badge: 'ĐẶC BIỆT',
  },
  'cold brew': {
    color: '#1565C0',
    gradient: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
    subtitle: 'Ủ lạnh thanh mát, đậm đà',
    iconName: 'ExperimentOutlined',
    badge: 'HOT',
  },
  'arabica base': {
    color: '#C67C4E',
    gradient: 'linear-gradient(135deg, #FFF3E0 0%, #FFE082 100%)',
    subtitle: 'Vị chua thanh, hương hoa quả',
    iconName: 'CoffeeOutlined',
  },
  'robusta base': {
    color: '#4E342E',
    gradient: 'linear-gradient(135deg, #EFEBE9 0%, #D7CCC8 100%)',
    subtitle: 'Đậm đà, gu truyền thống Việt',
    iconName: 'ThunderboltOutlined',
  },
  'chai': {
    color: '#AD1457',
    gradient: 'linear-gradient(135deg, #FCE4EC 0%, #F8BBD0 100%)',
    subtitle: 'Hương gia vị ấm áp độc đáo',
    iconName: 'BranchesOutlined',
  },
  'non caffein': {
    color: '#2E7D32',
    gradient: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
    subtitle: 'Trà và thức uống nhẹ nhàng',
    iconName: 'CloudOutlined',
    badge: 'HEALTHY',
  },
  'coffee': {
    color: '#A0522D',
    gradient: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)',
    subtitle: 'Hạt cà phê nguyên chất rang mộc',
    iconName: 'ShopOutlined',
  },
  'bottled-drinks': {
    color: '#00838F',
    gradient: 'linear-gradient(135deg, #E0F7FA 0%, #B2EBF2 100%)',
    subtitle: 'Đồ uống đóng chai tiện lợi',
    iconName: 'ShoppingOutlined',
  },
  'machines': {
    color: '#6A1B9A',
    gradient: 'linear-gradient(135deg, #F3E5F5 0%, #E1BEE7 100%)',
    subtitle: 'Dụng cụ và máy pha cà phê',
    iconName: 'ToolOutlined',
  },
  'news': {
    color: '#7C3AED',
    gradient: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)',
    subtitle: 'Bản tin & Chương trình ưu đãi',
    iconName: 'TagOutlined',
    badge: 'MỚI',
  },
};

export class MiniAppService {
  private iposDishService: DishService;
  private cukcukDishService: DishService;
  private groupService: GroupService;
  private equipmentService: CoffeeEquipmentService;

  constructor() {
    this.iposDishService = new DishService(IPOS_DISHES_COLLECTION);
    this.cukcukDishService = new DishService(CUKCUK_DISHES_COLLECTION);
    this.groupService = new GroupService();
    this.equipmentService = new CoffeeEquipmentService();
  }

  /**
   * Lấy cấu hình trang Sản phẩm từ Firestore
   */
  async getProductsPageConfig(): Promise<MiniAppProductsConfig> {
    try {
      const docRef = doc(db, CONFIG_COLLECTION, PRODUCTS_PAGE_DOC);
      const snapshot = await getDoc(docRef);

      if (snapshot.exists()) {
        const data = snapshot.data() as MiniAppProductsConfig;
        let categories = [...(data.categories || [])];
        if (!categories.some((c) => c.sourceType === 'news' || c.code === 'news')) {
          categories.push({
            id: 'cat_news',
            code: 'news',
            name: 'Tin tức & Ưu đãi',
            subtitle: 'Bản tin, chương trình ưu đãi mới nhất',
            sourceType: 'news',
            iconName: 'TagOutlined',
            colorTheme: '#7C3AED',
            gradient: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)',
            displayOrder: categories.length + 1,
            isActive: true,
            badge: 'HOT',
            displayLayout: 'horizontal_scroll',
            showInMainCategories: false,
            showAsFeaturedSection: true,
          });
        }
        return {
          ...this.getDefaultProductsConfig(),
          ...data,
          categories,
        };
      }
    } catch (error) {
      console.warn('Cannot fetch products page config from Firestore, using defaults:', error);
    }

    // Nếu chưa có trên Firestore, tạo cấu hình mặc định từ danh mục CUKCUK
    return this.generateDefaultProductsConfig();
  }

  /**
   * Lưu cấu hình trang Sản phẩm lên Firestore
   */
  async saveProductsPageConfig(config: MiniAppProductsConfig): Promise<void> {
    try {
      const docRef = doc(db, CONFIG_COLLECTION, PRODUCTS_PAGE_DOC);
      const cleanData = JSON.parse(JSON.stringify({
        ...config,
        updatedAt: new Date().toISOString(),
      }));
      await setDoc(docRef, cleanData, { merge: true });
    } catch (error) {
      console.error('Error saving products page config:', error);
      throw new Error('Không thể lưu cấu hình trang Sản phẩm: ' + error);
    }
  }

  /**
   * Lấy tất cả danh mục và món ăn thực tế từ CUKCUK & Thực đơn món
   */
  async fetchCukcukCategoryGroups(): Promise<CukcukCategoryGroup[]> {
    try {
      // 1. Lấy món từ cả cukcuk_dishes và 8am_dishes
      let allDishes: Dish[] = [];
      try {
        const cukcukDishes = await this.cukcukDishService.getAllDishes();
        if (cukcukDishes && cukcukDishes.length > 0) {
          allDishes = cukcukDishes;
        }
      } catch (e) {
        console.warn('Could not fetch cukcuk_dishes:', e);
      }

      if (allDishes.length === 0) {
        try {
          const iposDishes = await this.iposDishService.getAllDishes();
          allDishes = iposDishes;
        } catch (e) {
          console.warn('Could not fetch 8am_dishes:', e);
        }
      }

      // 2. Lấy danh sách nhóm từ 8am-group
      const groups = await this.groupService.getAllGroups();
      const groupCodeToNameMap = new Map<string, string>();
      groups.forEach((g) => {
        if (g.code) {
          groupCodeToNameMap.set(g.code.toLowerCase(), g.name);
          groupCodeToNameMap.set(g.name.toLowerCase(), g.name);
        }
      });

      // 3. Gom nhóm các món ăn theo Group / GroupName
      const categoryMap = new Map<string, { groupName: string; dishes: (Dish & { id: string })[] }>();

      // Đảm bảo các nhóm CUKCUK chuẩn luôn xuất hiện (Specialty, Cold Brew, Arabica Base, Robusta Base, Chai, Non Caffein)
      const standardGroupNames = [
        'Specialty',
        'Cold Brew',
        'Arabica Base',
        'Robusta Base',
        'Chai',
        'Non Caffein',
      ];

      standardGroupNames.forEach((name) => {
        const key = name.toLowerCase().trim();
        if (!categoryMap.has(key)) {
          categoryMap.set(key, { groupName: name, dishes: [] });
        }
      });

      // Thêm các món ăn vào nhóm tương ứng
      allDishes.forEach((dish) => {
        const rawGroup = dish.groupName || dish.group || 'Khác';
        const key = rawGroup.toLowerCase().trim();
        const displayName = groupCodeToNameMap.get(key) || rawGroup;

        if (!categoryMap.has(key)) {
          categoryMap.set(key, { groupName: displayName, dishes: [] });
        }

        const safeDish = {
          ...dish,
          id: dish.id || dish.code || `dish_${Math.random()}`,
        } as Dish & { id: string };

        categoryMap.get(key)!.dishes.push(safeDish);
      });

      // 4. Biến đổi thành danh sách CukcukCategoryGroup
      const result: CukcukCategoryGroup[] = [];

      categoryMap.forEach((val, key) => {
        const sampleDishWithImage = val.dishes.find((d) => d.imageUrl && d.imageUrl.trim() !== '');
        result.push({
          groupCode: key,
          groupName: val.groupName,
          dishCount: val.dishes.length,
          dishes: val.dishes,
          sampleImageUrl: sampleDishWithImage?.imageUrl || undefined,
        });
      });

      // Sắp xếp: ưu tiên các nhóm Cukcuk phổ biến trước
      return result.sort((a, b) => {
        const aIndex = standardGroupNames.findIndex((n) => n.toLowerCase() === a.groupName.toLowerCase());
        const bIndex = standardGroupNames.findIndex((n) => n.toLowerCase() === b.groupName.toLowerCase());
        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        return a.groupName.localeCompare(b.groupName);
      });
    } catch (error) {
      console.error('Error fetching Cukcuk category groups:', error);
      return [];
    }
  }

  /**
   * Tạo cấu hình mặc định phong phú cho trang Sản phẩm dựa trên menu CUKCUK
   */
  generateDefaultProductsConfig(): MiniAppProductsConfig {
    const cukcukCategories: MiniAppCategory[] = [
      {
        id: 'cukcuk_specialty',
        code: 'specialty',
        name: 'Specialty',
        subtitle: 'Cà phê hạt đặc sản chọn lọc',
        sourceType: 'cukcuk',
        cukcukGroupName: 'Specialty',
        iconName: 'FireOutlined',
        colorTheme: '#8B4513',
        gradient: 'linear-gradient(135deg, #FFF8E7 0%, #FFE0B2 100%)',
        displayOrder: 1,
        isActive: true,
        badge: 'ĐẶC BIỆT',
        displayLayout: 'horizontal_scroll',
        showInMainCategories: true,
        showAsFeaturedSection: true,
      },
      {
        id: 'cukcuk_cold_brew',
        code: 'cold-brew',
        name: 'Cold Brew',
        subtitle: 'Cà phê ủ lạnh thanh mát',
        sourceType: 'cukcuk',
        cukcukGroupName: 'Cold Brew',
        iconName: 'ExperimentOutlined',
        colorTheme: '#1565C0',
        gradient: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
        displayOrder: 2,
        isActive: true,
        badge: 'HOT',
        displayLayout: 'horizontal_scroll',
        showInMainCategories: true,
        showAsFeaturedSection: true,
      },
      {
        id: 'cukcuk_arabica_base',
        code: 'arabica-base',
        name: 'Arabica Base',
        subtitle: 'Nền Arabica chua thanh thanh tao',
        sourceType: 'cukcuk',
        cukcukGroupName: 'Arabica Base',
        iconName: 'CoffeeOutlined',
        colorTheme: '#C67C4E',
        gradient: 'linear-gradient(135deg, #FFF3E0 0%, #FFE082 100%)',
        displayOrder: 3,
        isActive: true,
        displayLayout: 'horizontal_scroll',
        showInMainCategories: true,
        showAsFeaturedSection: true,
      },
      {
        id: 'cukcuk_robusta_base',
        code: 'robusta-base',
        name: 'Robusta Base',
        subtitle: 'Nền Robusta đậm đà truyền thống',
        sourceType: 'cukcuk',
        cukcukGroupName: 'Robusta Base',
        iconName: 'ThunderboltOutlined',
        colorTheme: '#4E342E',
        gradient: 'linear-gradient(135deg, #EFEBE9 0%, #D7CCC8 100%)',
        displayOrder: 4,
        isActive: true,
        displayLayout: 'horizontal_scroll',
        showInMainCategories: true,
        showAsFeaturedSection: true,
      },
      {
        id: 'cukcuk_chai',
        code: 'chai',
        name: 'Chai',
        subtitle: 'Thức uống trà hương gia vị ấm áp',
        sourceType: 'cukcuk',
        cukcukGroupName: 'Chai',
        iconName: 'BranchesOutlined',
        colorTheme: '#AD1457',
        gradient: 'linear-gradient(135deg, #FCE4EC 0%, #F8BBD0 100%)',
        displayOrder: 5,
        isActive: true,
        displayLayout: 'horizontal_scroll',
        showInMainCategories: true,
        showAsFeaturedSection: true,
      },
      {
        id: 'cukcuk_non_caffein',
        code: 'non-caffein',
        name: 'Non Caffein',
        subtitle: 'Đồ uống không caffein lành mạnh',
        sourceType: 'cukcuk',
        cukcukGroupName: 'Non Caffein',
        iconName: 'CloudOutlined',
        colorTheme: '#2E7D32',
        gradient: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
        displayOrder: 6,
        isActive: true,
        badge: 'HEALTHY',
        displayLayout: 'horizontal_scroll',
        showInMainCategories: true,
        showAsFeaturedSection: true,
      },
      {
        id: 'cat_coffee_beans',
        code: 'coffee',
        name: 'Hạt cà phê',
        subtitle: 'Hạt rang mộc nguyên chất đóng gói',
        sourceType: 'coffee',
        iconName: 'ShopOutlined',
        colorTheme: '#A0522D',
        gradient: 'linear-gradient(135deg, #FFF3E0 0%, #FFCC80 100%)',
        displayOrder: 7,
        isActive: true,
        displayLayout: 'horizontal_scroll',
        showInMainCategories: true,
        showAsFeaturedSection: true,
      },
      {
        id: 'cat_bottled_drinks',
        code: 'bottled-drinks',
        name: 'Đồ uống đóng chai',
        subtitle: 'Cà phê tươi đóng chai mang đi tiện lợi',
        sourceType: 'bottledDrink',
        iconName: 'ShoppingOutlined',
        colorTheme: '#00838F',
        gradient: 'linear-gradient(135deg, #E0F7FA 0%, #80DEEA 100%)',
        displayOrder: 8,
        isActive: true,
        displayLayout: 'horizontal_scroll',
        showInMainCategories: true,
        showAsFeaturedSection: true,
      },
      {
        id: 'cat_coffee_equipment',
        code: 'machines',
        name: 'Dụng cụ cà phê',
        subtitle: 'Phễu V60, bình pha, máy xay cao cấp',
        sourceType: 'equipment',
        iconName: 'ToolOutlined',
        colorTheme: '#6A1B9A',
        gradient: 'linear-gradient(135deg, #F3E5F5 0%, #CE93D8 100%)',
        displayOrder: 9,
        isActive: true,
        displayLayout: 'horizontal_scroll',
        showInMainCategories: true,
        showAsFeaturedSection: true,
      },
      {
        id: 'cat_news',
        code: 'news',
        name: 'Tin tức & Ưu đãi',
        subtitle: 'Bản tin, chương trình ưu đãi mới nhất',
        sourceType: 'news',
        iconName: 'TagOutlined',
        colorTheme: '#7C3AED',
        gradient: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)',
        displayOrder: 10,
        isActive: true,
        badge: 'HOT',
        displayLayout: 'horizontal_scroll',
        showInMainCategories: false,
        showAsFeaturedSection: true,
      },
    ];

    return {
      headerTitle: 'Sản Phẩm',
      headerSubtitle: 'Thưởng thức cà phê chất lượng cao mỗi ngày',
      showSearchBar: true,
      showStoreSelector: true,
      showCategoriesSection: true,
      categorySectionTitle: 'Danh mục',
      categories: cukcukCategories,
      showFeaturedSection: true,
      featuredSectionTitle: 'Sản phẩm nổi bật',
    };
  }

  private getDefaultProductsConfig(): MiniAppProductsConfig {
    return this.generateDefaultProductsConfig();
  }

  /**
   * Tự động đồng bộ các danh mục mới nhất từ CUKCUK vào danh sách hiện tại
   */
  async syncCategoriesWithCukcuk(currentCategories: MiniAppCategory[]): Promise<MiniAppCategory[]> {
    const cukcukGroups = await this.fetchCukcukCategoryGroups();
    const existingCodes = new Set(currentCategories.map((c) => c.code.toLowerCase()));
    const existingNames = new Set(currentCategories.map((c) => c.name.toLowerCase()));

    const updatedCategories = [...currentCategories];
    let nextOrder = currentCategories.length > 0
      ? Math.max(...currentCategories.map((c) => c.displayOrder || 0)) + 1
      : 1;

    for (const group of cukcukGroups) {
      const codeKey = group.groupCode.toLowerCase();
      const nameKey = group.groupName.toLowerCase();

      if (!existingCodes.has(codeKey) && !existingNames.has(nameKey)) {
        const theme = DEFAULT_CATEGORY_THEMES[nameKey] || {
          color: '#D4713B',
          gradient: 'linear-gradient(135deg, #FFF8F0 0%, #FFE8D6 100%)',
          subtitle: `Thực đơn ${group.groupName}`,
        };

        updatedCategories.push({
          id: `cukcuk_${codeKey.replace(/[^a-z0-9]/g, '_')}`,
          code: codeKey,
          name: group.groupName,
          subtitle: theme.subtitle,
          sourceType: 'cukcuk',
          cukcukGroupName: group.groupName,
          imageUrl: group.sampleImageUrl,
          colorTheme: theme.color,
          gradient: theme.gradient,
          displayOrder: nextOrder++,
          isActive: true,
          badge: theme.badge,
          displayLayout: 'horizontal_scroll',
          showInMainCategories: true,
          showAsFeaturedSection: true,
        });
      }
    }

    if (!updatedCategories.some((c) => c.sourceType === 'news' || c.code === 'news')) {
      updatedCategories.push({
        id: 'cat_news',
        code: 'news',
        name: 'Tin tức & Ưu đãi',
        subtitle: 'Bản tin, chương trình ưu đãi mới nhất',
        sourceType: 'news',
        iconName: 'TagOutlined',
        colorTheme: '#7C3AED',
        gradient: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)',
        displayOrder: nextOrder++,
        isActive: true,
        badge: 'HOT',
        displayLayout: 'horizontal_scroll',
        showInMainCategories: false,
        showAsFeaturedSection: true,
      });
    }

    return updatedCategories;
  }

  /**
   * Lấy cấu hình các trang khác (Cho bạn, Tìm kiếm, Giỏ hàng, Cá nhân)
   */
  async getForYouPageConfig(): Promise<MiniAppForYouConfig> {
    try {
      const docRef = doc(db, CONFIG_COLLECTION, FOR_YOU_PAGE_DOC);
      const snapshot = await getDoc(docRef);
      if (snapshot.exists()) {
        return snapshot.data() as MiniAppForYouConfig;
      }
    } catch (e) {
      console.warn('Could not fetch foryou page config:', e);
    }
    return {
      headerTitle: 'Cho Bạn',
      showNotificationBell: true,
      showQRScanner: true,
      showStoreSelector: true,
      welcomeMessage: 'Chào bạn! Hôm nay bạn muốn thưởng thức ly cà phê gì?',
      sections: [
        { id: 'sec_banner', title: 'Banner truyền thông', type: 'banner', isActive: true, displayOrder: 1 },
        { id: 'sec_recommend', title: 'Gợi ý cho bạn', type: 'recommendations', isActive: true, displayOrder: 2 },
        { id: 'sec_favorites', title: 'Món bạn yêu thích', type: 'favorites', isActive: true, displayOrder: 3 },
        { id: 'sec_collections', title: 'Tuyển tập đặc sắc', type: 'collections', isActive: true, displayOrder: 4 },
        { id: 'sec_gifts', title: 'Quà tặng & Ưu đãi', type: 'gifts', isActive: true, displayOrder: 5 },
        { id: 'sec_history', title: 'Đã xem gần đây', type: 'history', isActive: true, displayOrder: 6 },
      ],
    };
  }

  async saveForYouPageConfig(config: MiniAppForYouConfig): Promise<void> {
    const docRef = doc(db, CONFIG_COLLECTION, FOR_YOU_PAGE_DOC);
    await setDoc(docRef, JSON.parse(JSON.stringify(config)), { merge: true });
  }

  async getSearchPageConfig(): Promise<MiniAppSearchConfig> {
    try {
      const docRef = doc(db, CONFIG_COLLECTION, SEARCH_PAGE_DOC);
      const snapshot = await getDoc(docRef);
      if (snapshot.exists()) {
        return snapshot.data() as MiniAppSearchConfig;
      }
    } catch (e) {
      console.warn('Could not fetch search config:', e);
    }
    return {
      headerTitle: 'Tìm Kiếm',
      searchPlaceholder: 'Tìm cà phê, món uống, dụng cụ...',
      maxRecentSearches: 10,
      enableVoiceSearch: true,
      trendingSearches: [
        'Cold Brew',
        'Catimor Trạm Hành',
        'Robusta Honey',
        'Specialty Pour Over',
        'V60 Dripper',
        'Espresso Arabica',
      ],
      suggestedTags: [
        'Cà phê đặc sản',
        'Ủ lạnh',
        'Rang mộc',
        'Pha phin',
        'Pha máy',
      ],
    };
  }

  async saveSearchPageConfig(config: MiniAppSearchConfig): Promise<void> {
    const docRef = doc(db, CONFIG_COLLECTION, SEARCH_PAGE_DOC);
    await setDoc(docRef, JSON.parse(JSON.stringify(config)), { merge: true });
  }

  async getCartPageConfig(): Promise<MiniAppCartConfig> {
    try {
      const docRef = doc(db, CONFIG_COLLECTION, CART_PAGE_DOC);
      const snapshot = await getDoc(docRef);
      if (snapshot.exists()) {
        return snapshot.data() as MiniAppCartConfig;
      }
    } catch (e) {
      console.warn('Could not fetch cart config:', e);
    }
    return {
      headerTitle: 'Giỏ Hàng',
      showStoreSelector: true,
      minOrderAmount: 0,
      freeShippingNoticeThreshold: 200000,
      upsellProductIds: [],
      suggestedNotes: [
        'Ít đá, ít đường',
        'Không đường',
        'Mang đi (Takeaway)',
        'Để đá riêng',
        'Xay hạt cà phê cho pha phin',
        'Xay hạt cà phê cho Pour Over / V60',
      ],
    };
  }

  async saveCartPageConfig(config: MiniAppCartConfig): Promise<void> {
    const docRef = doc(db, CONFIG_COLLECTION, CART_PAGE_DOC);
    await setDoc(docRef, JSON.parse(JSON.stringify(config)), { merge: true });
  }

  async getProfilePageConfig(): Promise<MiniAppProfileConfig> {
    try {
      const docRef = doc(db, CONFIG_COLLECTION, PROFILE_PAGE_DOC);
      const snapshot = await getDoc(docRef);
      if (snapshot.exists()) {
        return snapshot.data() as MiniAppProfileConfig;
      }
    } catch (e) {
      console.warn('Could not fetch profile config:', e);
    }
    return {
      headerTitle: 'Cá Nhân',
      showMembershipCard: true,
      hotline: '0901 888 888',
      address: '8am Coffee Roastery & Specialty Bar',
      policyLinks: [
        { title: 'Chính sách bảo mật', url: '/privacy-policy' },
        { title: 'Điều khoản dịch vụ', url: '/terms' },
        { title: 'Chính sách đổi trả', url: '/return-policy' },
      ],
      quickMenuItems: [
        { id: 'orders', title: 'Lịch sử đơn hàng', icon: 'FileTextOutlined', link: '/orders', isActive: true },
        { id: 'points', title: 'Lịch sử tích điểm', icon: 'StarOutlined', link: '/point-history', isActive: true },
        { id: 'vouchers', title: 'Kho voucher & ưu đãi', icon: 'GiftOutlined', link: '/voucher-history', isActive: true, badge: 'MỚI' },
        { id: 'qr', title: 'Mã QR hội viên', icon: 'QrcodeOutlined', link: '/qr-history', isActive: true },
        { id: 'settings', title: 'Cài đặt tài khoản', icon: 'SettingOutlined', link: '/settings', isActive: true },
      ],
    };
  }

  async saveProfilePageConfig(config: MiniAppProfileConfig): Promise<void> {
    const docRef = doc(db, CONFIG_COLLECTION, PROFILE_PAGE_DOC);
    await setDoc(docRef, JSON.parse(JSON.stringify(config)), { merge: true });
  }

  /**
   * Tải danh sách Hạt cà phê
   */
  async fetchCoffees() {
    try {
      return await coffeeService.getAllCoffees();
    } catch (e) {
      console.warn('Error fetching coffees:', e);
      return [];
    }
  }

  /**
   * Tải danh sách Đồ uống đóng chai
   */
  async fetchBottledDrinks() {
    try {
      return await bottledDrinkService.getAllBottledDrinks();
    } catch (e) {
      console.warn('Error fetching bottled drinks:', e);
      return [];
    }
  }

  /**
   * Tải danh sách Dụng cụ cà phê
   */
  async fetchEquipment() {
    try {
      return await this.equipmentService.getAllEquipment();
    } catch (e) {
      console.warn('Error fetching equipment:', e);
      return [];
    }
  }
}

export const miniAppService = new MiniAppService();
